import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";

import { cn } from "@/lib/utils";
import { track } from "@/utils/events/tracker";
import { ProjectRunMode, ReactFramework } from "@/utils/templateProjects";
import {
  getAxiosErorrMessage,
  logAxiosError,
  useActiveProject,
  useUserId,
} from "@/utils/utils";

import axios from "axios";
import {
  Check,
  CheckIcon,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Info,
  Loader2,
  Pencil,
  PlusIcon,
  Trash,
  X,
} from "lucide-react";
import { NavLink } from "react-router";
import { toast } from "sonner";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { useSession } from "@clerk/clerk-react";
import AnimatedToggle from "@/components/ui/animated-toggle";
import EnvVariableHandler from "@/components/EnvVariableHandler";
import CustomKnowledge from "../CustomKnowledge";
import { Tooltip } from "@mui/material";
import { GitHubLogoIcon } from "@radix-ui/react-icons";
import {
  GitProvider,
  getUniqueReposByOrganization,
} from "@/utils/gitProviderUtils";
import { useGitProvider } from "@/hooks/useGithubPermissions";
import PromptEditor from "../DesignSystemBuilder/components/DSPromptEditor";
import { useFeatureFlagEnabled } from "posthog-js/react";

interface EnvVariable {
  key: string;
  value: string;
  isModified?: boolean;
  originalValue?: string;
  isDeleted?: boolean;
}

interface GitHubRepo {
  name: string;
  owner: {
    organizations_url: string;
    avatar_url: string;
    full_name: string;
  };
}

interface Props {
  editProjectId?: string;
  editProjectData?: {
    name: string;
    repoPath: string;
    framework: string;
    mainFilePath: string;
    projectSubfolderPath: string;
    srcFolderPath: string;
    customInstallCommand: string;
    devServerPort: string;
    customRunCommand: string;
    customInstallExtraCommand: string;
    nodeVersion: string;
    packageManager: string;
    exampleComponentPath: string;
    disableAutoformat?: boolean;
    auto_install_off: boolean;
    base_branch?: string;
    provider: string;
    avatar_url: string;
    namespace: {
      name: string;
    };
  };
  close?: (project?: any, canvas?: any) => void;
  scrollAreaHeight?: string;
}

interface EnvVariableRowProps {
  envVariable: EnvVariable;
  originalIndex: number;
  visibleIndex: number;
  showDeleteButton: boolean;
  activelyEditingEnvVar: number | null;
  onSetEnvVariables: (updater: (prev: EnvVariable[]) => EnvVariable[]) => void;
  onSetActivelyEditingEnvVar: (index: number | null) => void;
  onSetShouldUpdateEnvVars: (value: boolean) => void;
}

const EnvVariableRow: React.FC<EnvVariableRowProps> = ({
  envVariable,
  originalIndex,
  visibleIndex,
  showDeleteButton,
  activelyEditingEnvVar,
  onSetEnvVariables,
  onSetActivelyEditingEnvVar,
  onSetShouldUpdateEnvVars,
}) => {
  const [isEditingKey, setIsEditingKey] = useState(false);
  const [tempKeyValue, setTempKeyValue] = useState(envVariable.key);
  const [isValueVisible, setIsValueVisible] = useState(false);

  // Sync tempKeyValue with envVariable.key when it changes
  useEffect(() => {
    if (!isEditingKey) {
      setTempKeyValue(envVariable.key);
    }
  }, [envVariable.key, isEditingKey]);

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempKeyValue(e.currentTarget.value);
  };

  const handleKeyEditStart = () => {
    setIsEditingKey(true);
    setTempKeyValue(envVariable.key);
  };

  const handleKeyEditCancel = () => {
    setIsEditingKey(false);
    setTempKeyValue(envVariable.key);
  };

  const handleKeyEditSave = () => {
    if (tempKeyValue !== envVariable.key) {
      onSetEnvVariables((prev) => {
        const copy = [...prev];

        // If this is an existing env var (has originalValue), mark it for deletion
        if (envVariable.originalValue) {
          copy[originalIndex].isDeleted = true;
          copy[originalIndex].isModified = true;

          // Add a new entry with the new key name and existing value
          copy.push({
            key: tempKeyValue,
            value: envVariable.value,
            isModified: true,
            originalValue: undefined,
            isDeleted: false,
          });
        } else {
          // If it's a new env var, just update the key
          copy[originalIndex].key = tempKeyValue;
          copy[originalIndex].isModified = true;
        }

        return copy;
      });
      onSetShouldUpdateEnvVars(true);
    }
    setIsEditingKey(false);
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSetEnvVariables((prev) => {
      const copy = [...prev];
      copy[originalIndex].value = e.currentTarget.value;
      copy[originalIndex].isModified = true;
      return copy;
    });
  };

  const handleValueFocus = () => {
    if (
      envVariable.originalValue &&
      !envVariable.isModified &&
      !isValueVisible
    ) {
      onSetActivelyEditingEnvVar(originalIndex);
      onSetEnvVariables((prev) => {
        const copy = [...prev];
        copy[originalIndex].value = "";
        return copy;
      });
    }
  };

  const handleValueBlur = () => {
    if (activelyEditingEnvVar === originalIndex && envVariable.value === "") {
      onSetEnvVariables((prev) => {
        const copy = [...prev];
        copy[originalIndex].value = copy[originalIndex].originalValue || "";
        copy[originalIndex].isModified = false;
        return copy;
      });
    }
    onSetActivelyEditingEnvVar(null);

    if (envVariable.key && envVariable.value) {
      onSetShouldUpdateEnvVars(true);
    }
  };

  const handleKeyBlur = () => {
    // Save the key changes when user finishes editing
    if (tempKeyValue !== envVariable.key) {
      onSetEnvVariables((prev) => {
        const copy = [...prev];
        copy[originalIndex].key = tempKeyValue;
        copy[originalIndex].isModified = true;
        return copy;
      });
    }

    if (tempKeyValue && envVariable.value) {
      onSetShouldUpdateEnvVars(true);
    }
  };

  const handleDelete = () => {
    if (envVariable.key) {
      onSetEnvVariables((prev) => {
        const copy = [...prev];
        copy[originalIndex].value = "";
        copy[originalIndex].isModified = true;
        copy[originalIndex].isDeleted = true;
        return copy;
      });
      onSetShouldUpdateEnvVars(true);
    } else {
      onSetEnvVariables((prev) => prev.filter((_, i) => i !== originalIndex));
    }
  };

  const isExistingEnvVar = !!envVariable.originalValue;

  return (
    <div className="flex items-end gap-2 px-4">
      <div className="w-full relative">
        {visibleIndex === 0 && (
          <span className="ml-2 text-secondary-n-400 text-2xs">Key</span>
        )}
        <div className="relative">
          <Input
            value={tempKeyValue}
            onChange={handleKeyChange}
            onBlur={handleKeyBlur}
            placeholder="api-key"
            disabled={isExistingEnvVar && !isEditingKey}
            className="text-xs placeholder:text-xs placeholder:text-secondary-n-600 w-full border border-transparent hover:border-secondary-n-600 focus:border-primary-0 rounded-md outline-none focus:outline-none h-8 justify-between px-2 font-normal bg-secondary-n-950 pr-10"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {isExistingEnvVar && (
              <>
                {isEditingKey ? (
                  <>
                    <X
                      onClick={handleKeyEditCancel}
                      className="w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer"
                    />
                    <Check
                      onClick={handleKeyEditSave}
                      className="w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer"
                    />
                  </>
                ) : (
                  <Pencil
                    onClick={handleKeyEditStart}
                    className="w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="w-full relative">
        {visibleIndex === 0 && (
          <span className="ml-2 text-secondary-n-400 text-2xs">Value</span>
        )}
        <div className="relative">
          <Input
            className="text-xs placeholder:text-xs placeholder:text-secondary-n-600 w-full border border-transparent hover:border-secondary-n-600 focus:border-primary-0 rounded-md outline-none focus:outline-none h-8 justify-between px-2 font-normal bg-secondary-n-950 pr-10"
            value={
              envVariable.originalValue &&
              !envVariable.isModified &&
              activelyEditingEnvVar !== originalIndex &&
              !isValueVisible
                ? "••••••••••••••"
                : envVariable?.value
            }
            onFocus={handleValueFocus}
            onChange={handleValueChange}
            onBlur={handleValueBlur}
            placeholder="key-value"
            aria-label="env-variable-value"
          />
          {envVariable.value && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <button
                onClick={() => setIsValueVisible(!isValueVisible)}
                className="p-0 border-none bg-transparent"
              >
                {isValueVisible ? (
                  <EyeOff className="w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer" />
                ) : (
                  <Eye className="w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer" />
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      {showDeleteButton && (
        <Button
          onClick={handleDelete}
          variant="outline"
          className="text-secondary-n-600 rounded-md border-none outline-none focus:outline-none h-8 justify-between px-2 font-normal bg-secondary-n-950"
        >
          <Trash className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
};

const EnvVariablesSection = React.memo<{
  envVariablesSet: EnvVariable[];
  activelyEditingEnvVar: number | null;
  onSetEnvVariables: (updater: (prev: EnvVariable[]) => EnvVariable[]) => void;
  onSetActivelyEditingEnvVar: (index: number | null) => void;
  onSetShouldUpdateEnvVars: (value: boolean) => void;
  handleEnvVariables: (variables: EnvVariable[]) => void;
}>(
  ({
    envVariablesSet,
    activelyEditingEnvVar,
    onSetEnvVariables,
    onSetActivelyEditingEnvVar,
    onSetShouldUpdateEnvVars,
    handleEnvVariables,
  }) => {
    const handleAddAnother = () => {
      onSetEnvVariables((prev) => {
        const copy = [...prev];
        copy.push({
          key: "",
          value: "",
          isModified: false,
          isDeleted: false,
        });
        return copy;
      });
    };

    const visibleEnvVars = useMemo(
      () =>
        envVariablesSet
          .map((envVariable, index) => ({ envVariable, originalIndex: index }))
          .filter(({ envVariable }) => !envVariable.isDeleted),
      [envVariablesSet]
    );

    return (
      <div className="mt-0 flex flex-col gap-3">
        {visibleEnvVars.map(({ envVariable, originalIndex }, visibleIndex) => {
          const showDeleteButton = visibleEnvVars.length > 1;
          return (
            <EnvVariableRow
              key={`${originalIndex}-${envVariable.key || "empty"}`}
              envVariable={envVariable}
              originalIndex={originalIndex}
              visibleIndex={visibleIndex}
              showDeleteButton={showDeleteButton}
              activelyEditingEnvVar={activelyEditingEnvVar}
              onSetEnvVariables={onSetEnvVariables}
              onSetActivelyEditingEnvVar={onSetActivelyEditingEnvVar}
              onSetShouldUpdateEnvVars={onSetShouldUpdateEnvVars}
            />
          );
        })}
        <div className="flex flex-col gap-4 px-4">
          <Button
            variant="outline"
            onClick={handleAddAnother}
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "var(--secondary-n-900)",
            }}
            size="lg"
            className="w-min whitespace-nowrap rounded-md px-2 text-xs font-medium h-8"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add another
          </Button>
        </div>
        <div className="border-t border-secondary-n-900 py-4 px-4">
          <EnvVariableHandler
            mode="upload"
            handleEnvVariables={handleEnvVariables}
            envVariablesSet={envVariablesSet}
          />
        </div>
      </div>
    );
  }
);

EnvVariablesSection.displayName = "EnvVariablesSection";

const ProjectSettings: React.FC<Props> = (props) => {
  const editProjectCandidateId = props.editProjectId ?? useActiveProject()?.id;

  const editProjectData = props.editProjectData ?? useActiveProject();

  const [projectNameInput, setProjectNameInput] = useState("");

  const [isOpen, setIsOpen] = useState(false);

  const [projectPathInput, setProjectPathInput] = useState("");

  const [projectPathSearch, setProjectPathSearch] = useState("");

  const [projectPathInputOpen, setProjectPathInputOpen] = useState(false);

  const [frameworkInput, setFrameworkInput] = useState<string>(
    ReactFramework.VITE
  );
  const [mainFilePathInput, setMainFilePathInput] = useState("");
  const [projectSubfolderPathInput, setProjectSubfolderPathInput] =
    useState("");
  const [srcFolderPath, setSrcFolderPath] = useState("");
  const [customInstallCommandInput, setCustomInstallCommandInput] =
    useState("");
  const [devServerPortInput, setDevServerPortInput] = useState("3000");
  const [customRunCommandInput, setCustomRunCommandInput] = useState("");
  const [customInstallExtraCommandInput, setCustomInstallExtraCommandInput] =
    useState("");
  const [nodeVersionInput, setNodeVersionInput] = useState("18");
  const [packageManagerInput, setPackageManagerInput] = useState("npm");
  const [exampleComponentPath, setExampleComponentPath] = useState("");
  const [autoFormatEnabled, setAutoformatEnabled] = useState(true);
  const [autoInstallOff, setAutoInstallOff] = useState(false);
  const [baseBranchInput, setBaseBranchInput] = useState("");

  const [requestedRemix, setRequestedRemix] = useState(false);

  const [isUpdatingProject, setIsUpdatingProject] = useState(false);
  const [isInitializing, setIsInitializing] = useState(
    !!editProjectCandidateId
  );

  const { session } = useSession();
  const { repos, reposLoading } = useGitProvider(editProjectData?.provider);

  const userId = useUserId();

  const [envVariablesSet, setEnvVariablesSet] = useState<EnvVariable[]>([
    {
      key: "",
      value: "",
      isModified: false,
      isDeleted: false,
    },
  ]);

  const [organizationPathInput, setOrganizationPathInput] = useState("");
  const [organizationPathSearch, setOrganizationPathSearch] = useState("");
  const [organizationPathInputOpen, setOrganizationPathInputOpen] =
    useState(false);
  const [uniqueRepos, setUniqueRepos] = useState<GitHubRepo[]>([]);

  const [editingStates, setEditingStates] = useState({});

  const [activelyEditingEnvVar, setActivelyEditingEnvVar] = useState<
    number | null
  >(null);

  const hasInitialized = useRef(false);

  const extractOrganization = useCallback((url: string): string | undefined => {
    const match = url.match(/\/users\/([^/]+\/[^/]+)$/);
    return match ? match[1] : undefined;
  }, []);

  const showDesignSystem = useFeatureFlagEnabled("custom_design_system");

  const inputsAndSetters = [
    {
      label: "Organization",
      value: organizationPathInput,
      setter: setOrganizationPathInput,
      optional: false,
      editableOnEdit: false,
      infoTooltip: "user/organization",
      renderHint: () => {
        return null;
      },
      renderInput: () => {
        if (reposLoading) {
          return (
            <Input
              disabled
              value="Loading Organizations..."
              className="text-xs border border-transparent hover:border-secondary-n-600 placeholder:text-xs placeholder:text-secondary-n-600 text-ellipsis text-secondary-n-50 line-clamp-1 w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950"
            />
          );
        }

        const provider = editProjectData?.provider || "github";

        if (editProjectCandidateId || !repos?.length) {
          if (provider === "gitlab") {
            const selectedRepo = repos.find(
              (repo) =>
                (repo as any).name?.toLocaleLowerCase() ===
                editProjectData?.repoPath?.toLocaleLowerCase()
            ) as any;
            const orgName = selectedRepo?.namespace?.name || "";
            const orgAvatar =
              selectedRepo?.namespace?.avatar_url || selectedRepo?.avatar_url;
            return (
              <div className="relative">
                <div className="flex items-center gap-1">
                  {orgAvatar ? (
                    <img
                      src={orgAvatar}
                      className="w-5 h-5 object-cover rounded-md absolute left-2 top-1/2 -translate-y-1/2"
                      alt=""
                    />
                  ) : (
                    <GitHubLogoIcon className="text-secondary-n-600 absolute left-2 top-1/2 -translate-y-1/2" />
                  )}
                  <Input
                    value={orgName}
                    onChange={() => {}}
                    disabled={true}
                    className="text-xs border border-transparent hover:border-secondary-n-600 placeholder:text-xs placeholder:text-secondary-n-600 text-ellipsis text-secondary-n-50 line-clamp-1 w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950 pl-8"
                  />
                </div>
                <Tooltip placement="top-end" title={"user/organization"}>
                  <Info
                    strokeWidth={1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 ml-2 h-4 w-4 shrink-0  text-secondary-n-400"
                  />
                </Tooltip>
              </div>
            );
          }
          return (
            <div className="relative">
              <div className="flex items-center gap-1">
                {provider === "gitlab" ? (
                  editProjectData?.avatar_url ? (
                    <img
                      src={editProjectData.avatar_url}
                      className="w-5 h-5 object-cover rounded-md absolute left-2 top-1/2 -translate-y-1/2"
                      alt=""
                    />
                  ) : (
                    <GitHubLogoIcon className="text-secondary-n-600 absolute left-2 top-1/2 -translate-y-1/2" />
                  )
                ) : organizationPathInput &&
                  uniqueRepos.find(
                    (repo) =>
                      extractOrganization(repo.owner.organizations_url) ===
                      organizationPathInput
                  ) ? (
                  <img
                    src={
                      uniqueRepos.find(
                        (repo) =>
                          extractOrganization(repo.owner.organizations_url) ===
                          organizationPathInput
                      )?.owner?.avatar_url
                    }
                    className="w-5 h-5 object-cover rounded-md absolute left-2 top-1/2 -translate-y-1/2"
                    alt=""
                  />
                ) : (
                  <GitHubLogoIcon className="text-secondary-n-600 absolute left-2 top-1/2 -translate-y-1/2" />
                )}
                <Input
                  value={
                    provider === "gitlab"
                      ? editProjectData?.namespace?.name || ""
                      : organizationPathInput
                  }
                  onChange={() => {}}
                  disabled={true}
                  className="text-xs border border-transparent hover:border-secondary-n-600 placeholder:text-xs placeholder:text-secondary-n-600 text-ellipsis text-secondary-n-50 line-clamp-1 w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950 pl-8"
                />
              </div>
              <Tooltip placement="top-end" title={"user/organization"}>
                <Info
                  strokeWidth={1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 ml-2 h-4 w-4 shrink-0  text-secondary-n-400"
                />
              </Tooltip>
            </div>
          );
        }

        return (
          <div className="flex flex-col space-y-0.5">
            <Popover
              open={organizationPathInputOpen}
              onOpenChange={setOrganizationPathInputOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="default"
                  role="combobox"
                  className={cn(
                    "w-full rounded-md h-8 justify-between px-2 font-normal border border-transparent hover:border-secondary-n-600 bg-secondary-n-950",
                    organizationPathInput && "text-md"
                  )}
                >
                  <div className="flex items-center gap-1">
                    {organizationPathInput &&
                    uniqueRepos.find(
                      (repo) =>
                        extractOrganization(repo.owner.organizations_url) ===
                        organizationPathInput
                    ) ? (
                      <img
                        src={
                          uniqueRepos.find(
                            (repo) =>
                              extractOrganization(
                                repo.owner.organizations_url
                              ) === organizationPathInput
                          )?.owner?.avatar_url
                        }
                        className="w-5 h-5 object-cover rounded-md"
                      />
                    ) : (
                      <GitHubLogoIcon className="text-secondary-n-600" />
                    )}
                    <span
                      className={cn(
                        "text-ellipsis text-secondary-n-50 text-xs line-clamp-1 text-left",
                        !organizationPathInput && "text-secondary-n-600"
                      )}
                    >
                      {organizationPathInput
                        ? organizationPathInput
                        : "Choose one"}
                    </span>
                  </div>

                  {organizationPathInputOpen ? (
                    <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  ) : (
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[250px] py-2 px-0 z-[3010] rounded-xl"
              >
                <Command className="pr-0 px-2">
                  <CommandInput
                    placeholder="Search organizations..."
                    value={organizationPathSearch}
                    onValueChange={setOrganizationPathSearch}
                    className="text-xs"
                  />
                  <CommandList className="mt-2">
                    <CommandGroup className="overflow-auto pr-0">
                      {uniqueRepos
                        .filter((repo) => {
                          const isExactMatch = uniqueRepos.some(
                            (r) =>
                              r.owner?.organizations_url ===
                              organizationPathSearch
                          );

                          if (isExactMatch) return true;

                          if (!organizationPathSearch) return true;

                          return repo?.owner?.organizations_url
                            ?.toLowerCase()
                            .includes(organizationPathSearch.toLowerCase());
                        })
                        .map((repo) => (
                          <CommandItem
                            className="text-xs px-2 gap-2 transition-colors duration-300 data-[selected=true]:bg-secondary-n-900 hover:bg-secondary-n-900"
                            key={repo?.owner?.organizations_url}
                            value={extractOrganization(
                              repo?.owner?.organizations_url
                            )}
                            onSelect={(currentValue) => {
                              setOrganizationPathInput(currentValue);
                              setOrganizationPathInputOpen(false);
                            }}
                          >
                            <img
                              src={repo?.owner?.avatar_url}
                              className="w-5 h-5 object-cover rounded-md"
                            />

                            {extractOrganization(
                              repo?.owner?.organizations_url
                            )}
                            <CheckIcon
                              className={cn(
                                "ml-auto h-4 w-4",
                                projectPathInput === repo.name
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      {organizationPathSearch && (
                        <CommandItem
                          className="text-xs"
                          value={organizationPathSearch}
                          onSelect={(currentValue) => {
                            setOrganizationPathInput(currentValue);
                            setOrganizationPathInputOpen(false);
                          }}
                        >
                          {organizationPathSearch}
                          <CheckIcon
                            className={cn(
                              "ml-auto h-4 w-4",
                              organizationPathInput === organizationPathSearch
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      )}
                    </CommandGroup>
                  </CommandList>
                </Command>
                {!props.editProjectId && provider === GitProvider.GITHUB && (
                  <div className="pt-2 border-t border-secondary-n-900">
                    <p className="text-center text-secondary-n-400 text-2xs font-medium">
                      Don't see the organization you're looking for?{" "}
                      <NavLink
                        className="text-2xs inline-block text-primary-n-600 font-bold hover:underline"
                        to="https://github.com/apps/tempolabs/installations/new"
                        target="_blank"
                      >
                        Install Tempo Github app
                      </NavLink>
                    </p>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        );
      },
    },
    {
      label: "Repository",
      value: projectPathInput,
      setter: setProjectPathInput,
      optional: false,
      editableOnEdit: false,
      infoTooltip: "user/repo",
      renderHint: () => {
        return null;
      },
      renderInput: () => {
        if (reposLoading) {
          return (
            <Input
              disabled
              value="Loading Repos..."
              className="text-xs border border-transparent hover:border-secondary-n-600 placeholder:text-xs placeholder:text-secondary-n-600 text-ellipsis text-secondary-n-50 line-clamp-1 w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950"
            />
          );
        }

        const provider = editProjectData?.provider || "github";

        if (editProjectCandidateId || !repos?.length) {
          return (
            <div className="relative">
              <Input
                value={
                  provider === "gitlab"
                    ? editProjectData?.name || ""
                    : projectPathInput
                }
                onChange={() => {}}
                disabled
                className={cn(
                  "text-xs border border-transparent hover:border-secondary-n-600 placeholder:text-xs placeholder:text-secondary-n-600 text-ellipsis text-secondary-n-50 line-clamp-1 w-full rounded-md h-8 justify-between px-2 font-normal bg-secondary-n-950"
                )}
              />
              <Tooltip placement="top-end" title={"user/repo"}>
                <Info
                  strokeWidth={1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 ml-2 h-4 w-4 shrink-0 text-secondary-n-400"
                />
              </Tooltip>
            </div>
          );
        }

        return (
          <div className="flex flex-col space-y-0.5">
            <Popover
              open={projectPathInputOpen}
              onOpenChange={setProjectPathInputOpen}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="default"
                  role="combobox"
                  className={cn(
                    "w-full rounded-md  h-8 justify-between px-2 font-normal border border-transparent hover:border-secondary-n-600 bg-secondary-n-950",
                    projectPathInput && "text-md"
                  )}
                >
                  <div className="flex items-center gap-1">
                    <GitHubLogoIcon className="text-secondary-n-600" />
                    <span
                      className={cn(
                        "text-ellipsis  text-secondary-n-50 text-xs line-clamp-1 text-left",
                        !projectPathInput && "text-secondary-n-600"
                      )}
                    >
                      {projectPathInput
                        ? projectPathInput
                        : "Select a repository"}
                    </span>
                  </div>
                  {projectPathInputOpen ? (
                    <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  ) : (
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[250px] py-2 px-0 z-[3010] rounded-xl"
              >
                <Command className="pr-0 px-2">
                  <CommandInput
                    placeholder="Search repository..."
                    value={projectPathSearch}
                    onValueChange={setProjectPathSearch}
                    className="text-xs placeholder:text-xs placeholder:text-secondary-n-600"
                  />
                  <CommandList className="mt-2">
                    <CommandGroup className="overflow-auto pr-0">
                      {repos
                        .filter((repo) => {
                          const isExactMatch = repos.some(
                            (r) => r.name === projectPathSearch
                          );

                          if (isExactMatch) return true;

                          if (!projectPathSearch) return true;

                          return repo.name
                            ?.toLowerCase()
                            .includes(projectPathSearch.toLowerCase());
                        })
                        .map((repo) => (
                          <CommandItem
                            className="text-xs px-2 transition-colors duration-300 data-[selected=true]:bg-secondary-n-900 hover:bg-secondary-n-900"
                            key={repo.name}
                            value={repo.name}
                            onSelect={(currentValue) => {
                              setProjectPathInput(currentValue);
                              // Find and set the organization avatar
                              const orgRepo = uniqueRepos.find(
                                (r) => r.owner?.full_name === currentValue
                              );
                              if (orgRepo) {
                                setOrganizationPathInput(
                                  extractOrganization(
                                    orgRepo.owner.organizations_url
                                  ) || ""
                                );
                              }

                              setProjectPathInputOpen(false);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <img
                                src={repo.owner?.avatar_url}
                                className="w-5 h-5 object-cover rounded-md"
                              />
                              {repo.name}
                            </div>
                            <CheckIcon
                              className={cn(
                                "ml-auto h-4 w-4",
                                projectPathInput === repo.name
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        );
      },
    },
    {
      label: "My Project",
      value: projectNameInput,
      setter: setProjectNameInput,
      optional: false,
      editableOnEdit: true,
      infoTooltip: "Project name",
    },
    {
      label: "Node Version ",
      value: nodeVersionInput,
      setter: setNodeVersionInput,
      optional: false,
      editableOnEdit: true,
      infoTooltip: "18 by default -> translates to node:18-alpine",
      renderInput: () => (
        <Select
          disabled
          onValueChange={(val) => {
            setNodeVersionInput(val);
          }}
          value={nodeVersionInput || undefined}
        >
          <SelectTrigger
            hideIcon
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
            }}
            className={
              "w-full rounded-md text-xs h-8 justify-between px-2 font-normal border border-transparent hover:border-secondary-n-600 bg-secondary-n-950"
            }
          >
            <SelectValue
              className="text-xs"
              placeholder="Select Node Version"
            />
            <Tooltip
              placement="top"
              title="If you want to change node version you have to create a new project"
            >
              <Info
                strokeWidth={1}
                className="ml-2 h-4 w-4 shrink-0 text-secondary-n-400"
              />
            </Tooltip>
          </SelectTrigger>
          <SelectContent>
            <SelectItem className="text-xs" value={"20"}>
              20
            </SelectItem>
            <SelectItem className="text-xs" value={"18"}>
              18
            </SelectItem>
            <SelectItem className="text-xs" value={"16"}>
              16
            </SelectItem>
            <SelectItem className="text-xs" value={"14"}>
              14
            </SelectItem>
            <SelectItem className="text-xs" value={"12"}>
              12
            </SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      label: "Framework",
      value: frameworkInput,
      setter: setFrameworkInput,
      optional: false,
      editableOnEdit: true,
      infoTooltip: "Select your framework",
      renderInput: () => (
        <Select
          disabled
          onValueChange={(val) => {
            if (val === ReactFramework.REMIX) {
              return;
            }

            setFrameworkInput(val);
          }}
          value={frameworkInput || undefined}
        >
          <SelectTrigger
            hideIcon
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
            }}
            className="w-full rounded-md text-xs h-8 justify-between px-2 font-normal border border-transparent hover:border-secondary-n-600 bg-secondary-n-950"
          >
            <SelectValue
              className="text-xs placeholder:text-xs placeholder:text-secondary-n-600"
              placeholder="Select Framework"
            />
            <Tooltip
              placement="top"
              title="If you want to change framework you have to create a new project"
            >
              <Info
                strokeWidth={1}
                className="ml-2 h-4 w-4 shrink-0 text-secondary-n-400"
              />
            </Tooltip>
          </SelectTrigger>
          <SelectContent>
            <SelectItem className="text-xs" value={ReactFramework.NEXT_JS}>
              Next JS (Pages Router)
            </SelectItem>
            <SelectItem
              className="text-xs"
              value={ReactFramework.NEXT_JS_APP_ROUTER}
            >
              Next JS (App Router)
            </SelectItem>
            <SelectItem className="text-xs" value={ReactFramework.VITE}>
              Vite
            </SelectItem>
            <SelectItem
              className="text-xs"
              value={ReactFramework.VITE_REACT_ROUTER_V7}
            >
              Vite React Router 7
            </SelectItem>
            <SelectItem
              className="text-xs"
              value={ReactFramework.VITE_WITH_VIKE}
            >
              Vite with Vike
            </SelectItem>
            <SelectItem className="text-xs" value={ReactFramework.EXPO}>
              Expo
            </SelectItem>
            <SelectItem disabled value={""} className="!opacity-100">
              <div className="flex flex-row items-center justify-between gap-2">
                <span className="opacity-50 text-xs">Remix (Coming Soon)</span>
                <Button
                  variant="primary"
                  size="xs"
                  className="pointer-events-auto !opacity-100 gap-1"
                  onClick={() => {
                    setRequestedRemix(true);
                    track("REQUEST_REMIX");
                  }}
                  disabled={requestedRemix}
                >
                  {requestedRemix && <CheckIcon className="w-3 h-3" />}
                  <span>{requestedRemix ? "Requested" : "Request"}</span>
                </Button>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      label: "Package Manager",
      value: packageManagerInput,
      setter: setPackageManagerInput,
      optional: false,
      editableOnEdit: true,
      renderInput: () => (
        <Select
          disabled
          onValueChange={(val) => {
            setPackageManagerInput(val);
          }}
          value={packageManagerInput || undefined}
        >
          <SelectTrigger
            hideIcon
            style={{
              borderWidth: "1px",
              borderStyle: "solid",
            }}
            className={
              "w-full rounded-md text-xs h-8 justify-between px-2 font-normal border border-transparent hover:border-secondary-n-600 bg-secondary-n-950"
            }
          >
            <SelectValue
              className="text-xs placeholder:text-xs placeholder:text-secondary-n-600"
              placeholder="Select Package Manager"
            />
            <Tooltip
              placement="top"
              title="If you want to change package manager you have to create a new project"
            >
              <Info
                strokeWidth={1}
                className="ml-2 h-4 w-4 shrink-0 text-secondary-n-400"
              />
            </Tooltip>
          </SelectTrigger>
          <SelectContent>
            <SelectItem className="text-xs" value={"npm"}>
              npm
            </SelectItem>
            <SelectItem className="text-xs" value={"yarn"}>
              yarn
            </SelectItem>
            <SelectItem className="text-xs" value={"bun"}>
              bun
            </SelectItem>
            <SelectItem className="text-xs" value={"pnpm"}>
              pnpm
            </SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      label: "Project Subfolder Path",
      value: projectSubfolderPathInput,
      setter: setProjectSubfolderPathInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "projects/client",
    },
    {
      label: "Main File Path",
      value: mainFilePathInput,
      setter: setMainFilePathInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "client/pages/_app.tsx",
    },
    {
      label: "Src File Path",
      value: srcFolderPath,
      setter: setSrcFolderPath,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "/client/src",
    },
    {
      label: "Example Component Path",
      value: exampleComponentPath,
      setter: setExampleComponentPath,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "/src/components/header.tsx",
    },
    {
      label: "Base Git Branch",
      value: baseBranchInput,
      setter: setBaseBranchInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "(dev)",
    },

    {
      label: "Dev Server Port",
      value: devServerPortInput,
      setter: setDevServerPortInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "3000",
    },
    {
      label: "Custom Install Command",
      value: customInstallCommandInput,
      setter: setCustomInstallCommandInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "npm run install-all-deps",
    },
    {
      label: "Custom Run Command",
      value: customRunCommandInput,
      setter: setCustomRunCommandInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "npm run client-start",
    },
    {
      label: "Custom Install Libraries Command",
      value: customInstallExtraCommandInput,
      setter: setCustomInstallExtraCommandInput,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "npm run add-and-log",
    },

    {
      label: "Automatically format all changes in Tempo",
      value: autoFormatEnabled,
      setter: setAutoformatEnabled,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "Automatically format all changes in Tempo",
      renderInput: () => {
        return (
          <AnimatedToggle
            icon={
              <X className="absolute top-[3px] left-[4px] w-3 h-3 text-xs font-medium" />
            }
            isOn={autoFormatEnabled}
            onChange={setAutoformatEnabled}
          />
        );
      },
    },
    {
      label: "Automatically install the dev tools package",
      value: autoInstallOff,
      setter: setAutoInstallOff,
      optional: true,
      editableOnEdit: true,
      infoTooltip: "Automatically install the dev tools package",
      renderInput: () => {
        return (
          <AnimatedToggle
            icon={
              <X className="absolute top-[3px] left-[4px] w-3 h-3 text-xs font-medium" />
            }
            isOn={autoInstallOff}
            onChange={setAutoInstallOff}
          />
        );
      },
    },
  ];

  const handleEditClick = useCallback((label: string) => {
    setEditingStates((prev) => ({ ...prev, [label]: true }));
  }, []);

  const handleSaveClick = useCallback(
    (label: string) => {
      updateProject();
      const input = inputsAndSetters.find((i) => i.label === label);
      setLastSavedValues((prev) => ({
        ...prev,
        [label]: input?.value,
      }));
      setEditingStates((prev) => ({ ...prev, [label]: false }));
    },
    [inputsAndSetters]
  );

  const [lastSavedValues, setLastSavedValues] = useState(() => {
    const initial = {};
    inputsAndSetters.forEach((input) => {
      initial[input.label] = input.value;
    });
    return initial;
  });

  useEffect(() => {
    const initial = {};
    inputsAndSetters.forEach((input) => {
      initial[input.label] = input.value;
    });
    setLastSavedValues(initial);
  }, [editProjectData]);

  useEffect(() => {
    if (!hasInitialized.current) return;
    updateProject();
  }, [
    packageManagerInput,
    nodeVersionInput,
    frameworkInput,
    autoFormatEnabled,
    autoInstallOff,
  ]);

  const [shouldUpdateEnvVars, setShouldUpdateEnvVars] = useState(false);

  useEffect(() => {
    const updateEnvVariables = async () => {
      try {
        const supabaseJWT = await session?.getToken();
        const environmentVariables = {};

        // Only send modified environment variables to avoid overwriting unchanged ones
        envVariablesSet
          .filter(
            (envVariable) =>
              envVariable.key &&
              (envVariable.isModified || envVariable.isDeleted)
          )
          .forEach((envVariable) => {
            if (envVariable.isDeleted || envVariable.value === "") {
              // Empty value or deleted flag means delete
              environmentVariables[envVariable.key] = null;
            } else {
              environmentVariables[envVariable.key] = envVariable.value;
            }
          });

        if (Object.keys(environmentVariables).length > 0) {
          await axios.post(
            `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/projects/${editProjectCandidateId}/envVariables`,
            environmentVariables,
            {
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
              },
            }
          );

          toast.success("Environment variables updated successfully");
        }

        setEnvVariablesSet((prev) =>
          prev
            .filter((env) => !env.isDeleted)
            .map((env) => ({
              ...env,
              isModified: false,
              originalValue: env.value,
            }))
        );
      } catch (err) {
        logAxiosError("Error updating environment variables", err);
        setShouldUpdateEnvVars(false);
        toast.error(
          `Failed to update environment variables. Error: ${getAxiosErorrMessage(
            err
          )}`
        );
      } finally {
        setShouldUpdateEnvVars(false);
      }
    };

    if (shouldUpdateEnvVars) {
      updateEnvVariables();
    }
  }, [shouldUpdateEnvVars]);

  useEffect(() => {
    if (!session || !editProjectData) {
      return;
    }

    // Repo loading is now handled by the useGitProvider hook automatically
    if (repos?.length) {
      const unique = getUniqueReposByOrganization(
        repos as unknown as GitHubRepo[]
      );
      setUniqueRepos(unique);
    }
  }, [Boolean(session), editProjectData]);

  useEffect(() => {
    if (editProjectData) {
      session?.getToken().then((supabaseJWT) => {
        axios
          .get(
            `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/projects/${editProjectCandidateId}/envVariables`,
            {
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
              },
            }
          )
          .then((res) => {
            const newEnvVariables: EnvVariable[] = [];
            Object.keys(res.data).forEach((key) => {
              newEnvVariables.push({
                key,
                value: res.data[key],
                isModified: false,
                originalValue: res.data[key],
                isDeleted: false,
              });
            });
            newEnvVariables.push({
              key: "",
              value: "",
              isModified: false,
              isDeleted: false,
            });

            setEnvVariablesSet(newEnvVariables);
          })
          .catch((err) => {
            toast.error("Failed to fetch env variables for project");
          })
          .finally(() => {
            setIsInitializing(false);
            hasInitialized.current = true;
          });
      });

      setProjectNameInput(editProjectData.name);
      setProjectPathInput(editProjectData.repoPath);
      setFrameworkInput(editProjectData.framework);
      setMainFilePathInput(editProjectData.mainFilePath);
      setProjectSubfolderPathInput(editProjectData.projectSubfolderPath);
      setSrcFolderPath(editProjectData.srcFolderPath);
      setCustomInstallCommandInput(editProjectData.customInstallCommand);
      setDevServerPortInput(editProjectData.devServerPort || "3000");
      setCustomRunCommandInput(editProjectData.customRunCommand);
      setCustomInstallExtraCommandInput(
        editProjectData.customInstallExtraCommand
      );
      setNodeVersionInput(editProjectData.nodeVersion || "18");
      setPackageManagerInput(editProjectData.packageManager || "npm");
      setExampleComponentPath(editProjectData.exampleComponentPath);
      setAutoformatEnabled(editProjectData.disableAutoformat !== true);
      setAutoInstallOff(editProjectData.auto_install_off);
      setBaseBranchInput(editProjectData.base_branch || "");
    }
  }, [editProjectData, editProjectCandidateId, Boolean(session)]);

  useEffect(() => {
    if (!repos?.length || !editProjectData?.repoPath) return;

    const provider = editProjectData.provider;
    let selectedRepo;
    if (provider === "gitlab") {
      selectedRepo = repos.find(
        (repo) =>
          (repo as any).name?.toLocaleLowerCase() ===
          editProjectData.repoPath.toLocaleLowerCase()
      ) as any;
      if (selectedRepo && selectedRepo.namespace?.name) {
        setOrganizationPathInput(selectedRepo.namespace.name);
      } else {
        setOrganizationPathInput("");
      }
    } else {
      selectedRepo = repos.find(
        (repo) =>
          (repo as any).owner?.full_name?.toLocaleLowerCase() ===
          editProjectData.repoPath
      );
      if (selectedRepo) {
        const org = extractOrganization(
          (selectedRepo as any).owner.organizations_url
        );
        if (org) {
          setOrganizationPathInput(org);
        }
      } else {
        setOrganizationPathInput("TempoLabsAI/orgs");
      }
    }
  }, [
    repos,
    editProjectData?.provider,
    editProjectData?.repoPath,
    extractOrganization,
  ]);

  const updateProject = () => {
    if (!projectNameInput) {
      toast.error("Missing project name input");
      return;
    }

    if (!projectPathInput) {
      toast.error("Missing project path input");
      return;
    }

    if (!frameworkInput) {
      toast.error("Missing framework input");
      return;
    }

    if (!nodeVersionInput) {
      toast.error("Missing node version input");
      return;
    }

    if (!packageManagerInput) {
      toast.error("Missing package manager input");
      return;
    }

    const urlToHit = `${
      import.meta.env.VITE_BACKEND_BASE_URL
    }/projects/${editProjectCandidateId}/edit`;
    track("EDIT_PROJECT", { projectId: editProjectCandidateId });

    setIsUpdatingProject(true);
    session?.getToken().then((supabaseJWT) => {
      axios
        .post(
          urlToHit,
          {
            repoName: projectNameInput,
            repoPath: projectPathInput,
            framework: frameworkInput,
            mainFilePath: mainFilePathInput,
            srcFolderPath: srcFolderPath,
            customInstallCommand: customInstallCommandInput,
            devServerPort: devServerPortInput,
            customRunCommand: customRunCommandInput,
            customInstallExtraCommand: customInstallExtraCommandInput,
            nodeVersion: nodeVersionInput,
            packageManager: packageManagerInput,
            userId: userId,
            exampleComponentPath,
            projectSubfolderPath: projectSubfolderPathInput,
            disableAutoformat: !autoFormatEnabled,
            runMode: ProjectRunMode.CLOUD,
            base_branch: baseBranchInput,
            auto_install_off: autoInstallOff,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          }
        )
        .then((res) => {
          const environmentVariables = {};
          // Only send modified environment variables to avoid overwriting unchanged ones
          envVariablesSet
            .filter(
              (envVariable) =>
                envVariable.key &&
                (envVariable.isModified || envVariable.isDeleted)
            )
            .forEach((envVariable) => {
              if (envVariable.isDeleted || envVariable.value === "") {
                // Empty value or deleted flag means delete
                environmentVariables[envVariable.key] = null;
              } else {
                environmentVariables[envVariable.key] = envVariable.value;
              }
            });

          const createdProject = res.data.project;
          const createCanvas = res.data.canvas;

          if (!Object.keys(environmentVariables).length) {
            toast.success("Edited project");
            if (!props.editProjectId) {
              props.close?.(createdProject, createCanvas);
            }
            return;
          }

          axios
            .post(
              `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${
                res.data?.project?.id
              }/envVariables`,
              environmentVariables,
              {
                headers: {
                  Authorization: `Bearer ${supabaseJWT}`,
                },
              }
            )
            .then((res) => {
              toast.success("Edited project");
              if (!props.editProjectId) {
                props.close?.(createdProject, createCanvas);
              }
              return;
            })
            .catch((err) => {
              logAxiosError(
                "Error setting environment variables for edited project",
                err
              );
              toast.error(
                `Could not edit project, check if path is valid. Error: ${getAxiosErorrMessage(
                  err
                )}`
              );
            });
        })
        .catch((err) => {
          logAxiosError("Error editing project", err);
          toast.error(
            `Could not edit project, check if path is valid. Error: ${getAxiosErorrMessage(
              err
            )}`
          );
        })
        .finally(() => {
          setIsUpdatingProject(false);
        });
    });
  };

  const handleEnvVariables = useCallback((variables: EnvVariable[]) => {
    setEnvVariablesSet(variables);
    setShouldUpdateEnvVars(true);
  }, []);

  return (
    <div className="w-full">
      <div className="w-full">
        <div className="max-w-2xlr">
          <Tabs
            variant="underline"
            defaultValue={"configuration"}
            className="w-full gap-2 relative"
          >
            <TabsList className="w-full mb-2 gap-4 px-4">
              <TabsTrigger className="text-xs" value={"configuration"}>
                Configuration
              </TabsTrigger>
              <TabsTrigger className="text-xs" value={"env-variables"}>
                Env Vars
              </TabsTrigger>
              <TabsTrigger className="text-xs" value={"custom-knowledge"}>
                Custom Knowledge
              </TabsTrigger>
            </TabsList>
            {isUpdatingProject ||
              (isInitializing && (
                <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-lg">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">
                      {isUpdatingProject
                        ? "Saving project..."
                        : "Loading project data..."}
                    </span>
                  </div>
                </div>
              ))}
            <ScrollArea
              className={props.scrollAreaHeight || "h-[calc(100vh-120px)]"}
            >
              <div className="p-[1px]">
                <TabsContent
                  className="mt-0 flex flex-col gap-3 w-full px-4"
                  value="configuration"
                >
                  <div>
                    <div className="flex flex-col gap-1">
                      {inputsAndSetters.map((inputAndSetter) => {
                        if (inputAndSetter.optional) return;

                        const isEditing =
                          editingStates[inputAndSetter.label] || false;
                        const hasValue = !!inputAndSetter.value;

                        return (
                          <div className="relative" key={inputAndSetter.label}>
                            <div>
                              {inputAndSetter.label && (
                                <span className="ml-2 text-secondary-n-400 text-2xs font-medium">
                                  {inputAndSetter.label}
                                </span>
                              )}

                              {inputAndSetter.renderInput ? (
                                <div className="mb-1">
                                  {inputAndSetter.renderInput()}
                                </div>
                              ) : (
                                <div className="flex gap-2 items-center pt-0 w-full border pr-2 border-transparent hover:border-secondary-n-600 focus-within:!border-primary-0 rounded-md outline-none justify-between bg-secondary-n-950">
                                  <Input
                                    variant="unstyled"
                                    value={inputAndSetter.value?.toString?.()}
                                    onChange={(e) => {
                                      inputAndSetter.setter(e.target.value);
                                      setEditingStates((prev) => ({
                                        ...prev,
                                        [inputAndSetter.label]: true,
                                      }));
                                    }}
                                    placeholder={
                                      inputAndSetter?.infoTooltip || ""
                                    }
                                    disabled={!inputAndSetter.editableOnEdit}
                                    className="w-full h-8 px-2 font-normal text-xs justify-between !border-none outline-none ring-0 focus:ring-0 focus:outline-none bg-transparent focus:bg-transparent placeholder:text-secondary-n-600"
                                  />
                                  <div>
                                    {isEditing ? (
                                      <div className="flex items-center gap-2">
                                        <X
                                          onClick={() => {
                                            inputAndSetter.setter(
                                              lastSavedValues[
                                                inputAndSetter.label
                                              ] || ""
                                            );
                                            setEditingStates((prev) => ({
                                              ...prev,
                                              [inputAndSetter.label]: false,
                                            }));
                                          }}
                                          className="w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer"
                                        />
                                        <Check
                                          onClick={() =>
                                            handleSaveClick(
                                              inputAndSetter.label
                                            )
                                          }
                                          className={cn(
                                            "w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer",
                                            isEditing && "text-secondary-n-50"
                                          )}
                                        />
                                      </div>
                                    ) : (
                                      hasValue && (
                                        <div
                                          className="cursor-pointer"
                                          onClick={() =>
                                            handleEditClick(
                                              inputAndSetter.label
                                            )
                                          }
                                        >
                                          <Pencil className="w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer" />
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {inputAndSetter.renderHint
                              ? inputAndSetter.renderHint()
                              : null}
                          </div>
                        );
                      })}
                    </div>
                    <Collapsible
                      open={isOpen}
                      onOpenChange={(value) => setIsOpen(value)}
                      className="w-full border-2 border-secondary-n-950 rounded-md mt-2"
                    >
                      <CollapsibleTrigger className="w-full">
                        <Button
                          variant="outline"
                          role="combobox"
                          className={cn(
                            "w-full rounded-md h-8 justify-between px-3 bg-secondary-n-1000 border border-secondary-n-950",
                            projectPathInput && "text-md"
                          )}
                        >
                          <span className="text-ellipsis text-secondary-n-50 text-xs line-clamp-1 text-left">
                            Advanced
                          </span>
                          {isOpen ? (
                            <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          ) : (
                            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="gap-4 px-2 pb-2">
                          {inputsAndSetters.map((inputAndSetter) => {
                            if (!inputAndSetter.optional) return;

                            const isEditing =
                              editingStates[inputAndSetter.label] || false;
                            const hasValue = !!inputAndSetter.value;

                            const originalValue =
                              editProjectData?.[
                                inputAndSetter.label
                                  .replace(/\s/g, "")
                                  .charAt(0)
                                  .toLowerCase() +
                                  inputAndSetter.label
                                    .replace(/\s/g, "")
                                    .slice(1)
                              ] || "";

                            return (
                              <div
                                className={cn(
                                  inputAndSetter.renderInput &&
                                    "flex items-center justify-between mt-1"
                                )}
                                key={inputAndSetter.label}
                              >
                                <span
                                  className={cn(
                                    "ml-2",
                                    inputAndSetter.renderInput
                                      ? "text-secondary-n-50 text-xs mr-2"
                                      : "text-secondary-n-400 text-2xs font-medium"
                                  )}
                                >
                                  {inputAndSetter.label}
                                </span>
                                {inputAndSetter.renderInput ? (
                                  <div className="mt-2">
                                    {inputAndSetter.renderInput()}
                                  </div>
                                ) : (
                                  <div className="flex gap-2 items-center pt-0 w-full border pr-2 border-transparent hover:border-secondary-n-600 focus-within:!border-primary-0 rounded-md outline-none justify-between bg-secondary-n-950">
                                    <Input
                                      variant="unstyled"
                                      value={inputAndSetter.value?.toString?.()}
                                      onChange={(e) => {
                                        inputAndSetter.setter(e.target.value);
                                        setEditingStates((prev) => ({
                                          ...prev,
                                          [inputAndSetter.label]: true,
                                        }));
                                      }}
                                      placeholder={
                                        inputAndSetter?.infoTooltip || ""
                                      }
                                      disabled={!inputAndSetter.editableOnEdit}
                                      className="w-full h-8 px-2 font-normal text-xs justify-between !border-none outline-none ring-0 focus:ring-0 focus:outline-none bg-transparent focus:bg-transparent placeholder:text-secondary-n-600"
                                    />
                                    <div>
                                      {isEditing ? (
                                        <div className="flex items-center gap-2">
                                          <X
                                            onClick={() => {
                                              inputAndSetter.setter(
                                                lastSavedValues[
                                                  inputAndSetter.label
                                                ] || ""
                                              );
                                              setEditingStates((prev) => ({
                                                ...prev,
                                                [inputAndSetter.label]: false,
                                              }));
                                            }}
                                            className="w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer"
                                          />
                                          <Check
                                            onClick={() => {
                                              updateProject();
                                              setLastSavedValues((prev) => ({
                                                ...prev,
                                                [inputAndSetter.label]:
                                                  inputAndSetter.value,
                                              }));
                                              setEditingStates((prev) => ({
                                                ...prev,
                                                [inputAndSetter.label]: false,
                                              }));
                                            }}
                                            className={cn(
                                              "w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer",
                                              isEditing && "text-secondary-n-50"
                                            )}
                                          />
                                        </div>
                                      ) : (
                                        hasValue && (
                                          <div
                                            className="cursor-pointer"
                                            onClick={() =>
                                              setEditingStates((prev) => ({
                                                ...prev,
                                                [inputAndSetter.label]: true,
                                              }))
                                            }
                                          >
                                            <Pencil className="w-4 h-4 text-secondary-n-600 hover:text-secondary-n-50 cursor-pointer" />
                                          </div>
                                        )
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                </TabsContent>
                <TabsContent
                  className="mt-0 flex flex-col gap-3 w-full"
                  value="env-variables"
                >
                  <EnvVariablesSection
                    envVariablesSet={envVariablesSet}
                    activelyEditingEnvVar={activelyEditingEnvVar}
                    onSetEnvVariables={setEnvVariablesSet}
                    onSetActivelyEditingEnvVar={setActivelyEditingEnvVar}
                    onSetShouldUpdateEnvVars={setShouldUpdateEnvVars}
                    handleEnvVariables={handleEnvVariables}
                  />
                </TabsContent>
                <TabsContent
                  className="mt-0 flex flex-col gap-6 w-full"
                  value="custom-knowledge"
                >
                  <CustomKnowledge />
                  {showDesignSystem && <PromptEditor />}
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProjectSettings);
